import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiLink from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { isApiError } from "@bw/shared/api";
import { notifications } from "@/app/notifications";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  createPricingRule,
  deletePricingRule,
  updatePricingRule,
  type ChargeUnit,
  type PricingRule,
  type PricingRuleCreateInput,
  type PricingRuleUpdateInput,
  type PricingService,
} from "@/libs/ratesApi";
import { HHMM, MONEY } from "./schema";
import {
  paymentReadinessAction,
  type PaymentReadinessAction,
} from "./payment-readiness-action";

// Simple rates mode — a GUIDED FORM for the 90% operator. They type ONE ABSOLUTE
// price per common band and Save GENERATES real engine rules tagged
// source:"simple" so they never touch the Advanced rule list. The full Advanced
// editor + the powerful pricing engine stay underneath, untouched.
//
// The FE is a THIN presentation layer: it owns NO pricing math. Each band's
// base_price is sent VERBATIM (the operator's typed Decimal string) — there is
// no base±delta arithmetic, no derived peak/member figure. The backend owns
// price computation, eligibility, and precedence. Simple mode only decides which
// CONDITION fields (all_day / start_time / end_time / repeat_type /
// membership_scope) each band maps to; the price is never computed here.
//
// Round-trip + the Advanced/custom split (the important correctness bit):
//   * Load: the parent's rules are split by source. source:"simple" rules PREFILL
//     the matching band by NAME ("Standard rate" / "Peak hours" / "Weekend" /
//     "Member rate"). source:"advanced" rules are shown ONLY as a read-only
//     "Custom rules (N) — edit in Advanced" notice — Simple mode NEVER edits or
//     clobbers them (no advanced rule_id is ever passed to create/update/delete).
//   * Save: Simple touches ONLY its own source:"simple" rules — CREATE a newly
//     filled band, UPDATE a changed prefilled band (keyed by its rule_id, never a
//     duplicate create), DELETE a cleared prefilled optional band. After save →
//     refetch (the server is authoritative).

// The four bands, in priority-index order. The base priority offset keeps Simple
// rules out of the low Advanced slots in the demo data; the backend's specificity
// tiebreak is what actually decides precedence — Simple only needs DISTINCT slots
// (assigned by band index here).
const PRIORITY_BASE = 100;

type BandKey = "standard" | "peak" | "weekend" | "member";

interface BandDef {
  key: BandKey;
  name: string;
  // The price input's accessible label (matches the test regexes).
  priceLabel: string;
  optional: boolean;
}

// A per-service preset: which pricing SERVICE the generated rules target, how the
// price is charged (charge_unit), and which BANDS the guided form exposes. This is
// a "presets over the ONE pricing engine" facade — the same round-trip and rule
// planner, re-pointed at a different service. The default preset preserves the
// court_reservation Simple form byte-for-byte so the rates-workspace caller is
// unchanged.
export interface SimpleRatesServiceConfig {
  service: PricingService;
  chargeUnit: ChargeUnit;
  bands: BandDef[];
}

// Court reservation (the DEFAULT) — per-hour, all four bands incl. Member.
const COURT_RESERVATION_BANDS: BandDef[] = [
  {
    key: "standard",
    name: "Standard rate",
    priceLabel: "Standard rate (price)",
    optional: false,
  },
  {
    key: "peak",
    name: "Peak hours",
    priceLabel: "Peak hours (price)",
    optional: true,
  },
  {
    key: "weekend",
    name: "Weekend",
    priceLabel: "Weekend (price)",
    optional: true,
  },
  {
    key: "member",
    name: "Member rate",
    priceLabel: "Member rate (price)",
    optional: true,
  },
];

export const COURT_RESERVATION_SIMPLE_CONFIG: SimpleRatesServiceConfig = {
  service: "court_reservation",
  chargeUnit: "per_hour",
  bands: COURT_RESERVATION_BANDS,
};

// Drop-in — per-SESSION, and NO Member band: the backend's
// validate_open_play_is_simple rejects a rule with membership_scope != "any" (a
// 422), so a member drop-in price needs a backend change first. It is surfaced as
// a named blocker in the Drop-in service home's pricing card, NOT a control that
// silently 422s (no-stubs). Standard / Peak / Weekend map to the same conditions
// as court_reservation; only the service + charge_unit differ.
const OPEN_PLAY_BANDS: BandDef[] = [
  {
    key: "standard",
    name: "Standard rate",
    priceLabel: "Standard rate (price)",
    optional: false,
  },
  {
    key: "peak",
    name: "Peak hours",
    priceLabel: "Peak hours (price)",
    optional: true,
  },
  {
    key: "weekend",
    name: "Weekend",
    priceLabel: "Weekend (price)",
    optional: true,
  },
];

export const OPEN_PLAY_SIMPLE_CONFIG: SimpleRatesServiceConfig = {
  service: "open_play",
  chargeUnit: "per_session",
  bands: OPEN_PLAY_BANDS,
};

// The CONDITION fields each band maps to. The price + (peak) the time window are
// the only operator inputs; everything else is fixed per band. NO price math.
function bandConditions(key: BandKey): Partial<PricingRuleCreateInput> {
  switch (key) {
    case "standard":
      // Day-agnostic all-day fallback. NOT weekend/weekday-restricted.
      return { all_day: true, repeat_type: "never", membership_scope: "any" };
    case "peak":
      // Windowed — NOT all_day. Times come from the operator (start_time/end_time
      // added at build time). Day-agnostic otherwise.
      return { all_day: false, repeat_type: "never", membership_scope: "any" };
    case "weekend":
      // repeat_type "weekends" is the WRITABLE field (the backend maps it to
      // days_of_week [5,6]); "day_type weekend" is NOT a Create field.
      return {
        all_day: true,
        repeat_type: "weekends",
        membership_scope: "any",
      };
    case "member":
      return {
        all_day: true,
        repeat_type: "never",
        membership_scope: "any_member",
      };
  }
}

// How the charge unit reads in operator help copy. The only price-facing bit of
// copy that varies by preset ("per hour" for court_reservation, "per session" for
// drop-in). No pricing math — a plain label.
function chargeUnitPhrase(unit: ChargeUnit): string {
  switch (unit) {
    case "per_hour":
      return "per hour";
    case "per_session":
      return "per session";
    case "flat":
      return "per booking";
  }
}

// One-line operator help per band. Standard's copy keys off the charge unit; the
// optional bands are unit-agnostic.
function bandHelp(key: BandKey, unit: ChargeUnit): string {
  switch (key) {
    case "standard":
      return `The day-agnostic base price players pay ${chargeUnitPhrase(unit)}.`;
    case "peak":
      return "A higher price during a busy window. Leave blank to skip.";
    case "weekend":
      return "A weekend price (Saturday + Sunday). Leave blank to skip.";
    case "member":
      return "A price for any member, regardless of plan. Leave blank to skip.";
  }
}

// Per-band form state: the typed price + (peak only) the time window. Each value
// is the operator's input VERBATIM — never a computed figure.
interface BandState {
  price: string;
  startTime: string;
  endTime: string;
}

const EMPTY_BAND: BandState = { price: "", startTime: "", endTime: "" };

export interface SimpleRatesModeProps {
  facilityId: string;
  // The facility's pricing rules (server order). Both Simple and Advanced read
  // the SAME rules — switching modes is lossless.
  rules: PricingRule[];
  currency: string;
  // Re-read the authoritative rules after a successful save.
  onSaved: () => void | Promise<void>;
  // Which service this Simple form drives (presets over the one engine). Defaults
  // to the court_reservation / per_hour / 4-band shape so the rates-workspace
  // caller is unchanged.
  config?: SimpleRatesServiceConfig;
  disabled?: boolean;
}

export function SimpleRatesMode({
  facilityId,
  rules,
  currency,
  onSaved,
  config = COURT_RESERVATION_SIMPLE_CONFIG,
  disabled = false,
}: SimpleRatesModeProps) {
  const bandDefs = config.bands;
  // Each prefilled band remembers the source:"simple" rule it came from, so an
  // edit UPDATEs that id (not a duplicate create) and a clear DELETEs that id.
  // Keyed by band — only a source:"simple" rule of THIS service whose NAME matches
  // the band. The `r.service === config.service` filter is the cross-service
  // collision fence: a court_reservation "Standard rate" must NEVER prefill the
  // open_play Standard band (they share a name, not a service).
  const simpleRuleByBand = useMemo(() => {
    const map = new Map<BandKey, PricingRule>();
    for (const band of bandDefs) {
      const match = rules.find(
        (r) =>
          (r.source ?? "advanced") === "simple" &&
          r.service === config.service &&
          r.name === band.name,
      );
      if (match) map.set(band.key, match);
    }
    return map;
  }, [rules, bandDefs, config.service]);

  // Advanced (custom) rules are shown only as a read-only count — Simple NEVER
  // touches them. A rule with no `source` defaults to "advanced".
  const advancedCount = useMemo(
    () => rules.filter((r) => (r.source ?? "advanced") !== "simple").length,
    [rules],
  );

  const [bands, setBands] = useState<Record<BandKey, BandState>>({
    standard: { ...EMPTY_BAND },
    peak: { ...EMPTY_BAND },
    weekend: { ...EMPTY_BAND },
    member: { ...EMPTY_BAND },
  });
  const [error, setError] = useState<string | null>(null);
  const [readinessAction, setReadinessAction] =
    useState<PaymentReadinessAction | null>(null);
  const [saving, setSaving] = useState(false);
  const saveInFlight = useRef(false);

  // Prefill the bands from the existing source:"simple" rules whenever the rules
  // change (on load + after a save refetch). The input value is the rule's
  // base_price VERBATIM (a raw Decimal string) — NOT formatMoney; the operator
  // edits the same shape they typed.
  useEffect(() => {
    setBands({
      standard: prefill("standard"),
      peak: prefill("peak"),
      weekend: prefill("weekend"),
      member: prefill("member"),
    });
    setError(null);
    setReadinessAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules]);

  function prefill(key: BandKey): BandState {
    const rule = simpleRuleByBand.get(key);
    if (!rule) return { ...EMPTY_BAND };
    return {
      price: String(rule.base_price ?? ""),
      startTime: rule.start_time ?? "",
      endTime: rule.end_time ?? "",
    };
  }

  const setBand = (key: BandKey, patch: Partial<BandState>) => {
    setBands((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  // Build the Create/Update body for a filled band. base_price is the typed
  // value VERBATIM; the band's fixed condition fields + (peak) the time window.
  const buildBody = (
    band: BandDef,
    state: BandState,
    index: number,
  ): PricingRuleCreateInput => {
    const conditions = bandConditions(band.key);
    const body: PricingRuleCreateInput = {
      service: config.service,
      base_price: state.price.trim(),
      currency,
      charge_unit: config.chargeUnit,
      // Tagged source:"simple" so it round-trips back into THIS band and can
      // never masquerade as a custom (advanced) rule.
      source: "simple",
      name: band.name,
      // Distinct priority slot per band index (backend tiebreak owns specificity).
      priority: PRIORITY_BASE + index,
      all_day: conditions.all_day ?? true,
      repeat_type: conditions.repeat_type ?? "never",
      membership_scope: conditions.membership_scope ?? "any",
    };
    // Peak is windowed — send the operator's typed time window.
    if (band.key === "peak") {
      body.start_time = state.startTime.trim();
      body.end_time = state.endTime.trim();
    }
    return body;
  };

  const handleSave = async () => {
    if (disabled || saveInFlight.current) return;
    setError(null);
    setReadinessAction(null);

    // Money-shape gate (presence/shape only — the backend owns the authoritative
    // pricing rules). A filled band MUST be a clean ≤2-decimal amount; a >2-decimal
    // or otherwise malformed price blocks the save with NO API call.
    for (const band of bandDefs) {
      const price = bands[band.key].price.trim();
      if (price !== "" && !MONEY.test(price)) {
        setError("Enter a valid amount (up to 2 decimal places).");
        return;
      }
    }
    // Peak, when given a price, needs a valid time window (presence/shape only).
    const peak = bands.peak;
    if (peak.price.trim() !== "") {
      if (
        !HHMM.test(peak.startTime.trim()) ||
        !HHMM.test(peak.endTime.trim())
      ) {
        setError("Enter a valid peak start and end time (HH:MM).");
        return;
      }
    }

    // Plan the writes. For each band:
    //   filled + prefilled  → UPDATE by the simple rule's id
    //   filled + new        → CREATE
    //   cleared + prefilled → DELETE the simple rule's id
    //   never filled        → nothing
    // No advanced id is ever in this plan.
    type Op =
      | { kind: "create"; body: PricingRuleCreateInput }
      | { kind: "update"; ruleId: string; body: PricingRuleUpdateInput }
      | { kind: "delete"; ruleId: string };
    const ops: Op[] = [];

    bandDefs.forEach((band, index) => {
      const state = bands[band.key];
      const filled = state.price.trim() !== "";
      const existing = simpleRuleByBand.get(band.key);
      if (filled) {
        const body = buildBody(band, state, index);
        if (existing) {
          const { priority: _createPriority, ...updateBody } = body;
          ops.push({
            kind: "update",
            ruleId: existing.rule_id,
            body: updateBody,
          });
        } else {
          ops.push({ kind: "create", body });
        }
      } else if (existing) {
        // The optional band was prefilled and is now cleared → delete ONLY that
        // simple rule. A never-filled band creates nothing.
        ops.push({ kind: "delete", ruleId: existing.rule_id });
      }
    });

    if (ops.length === 0) {
      setError("Enter a Standard rate to save.");
      return;
    }

    // Deletes (cleared optional bands) are attempted FIRST, then updates, then
    // creates. Every op is INDEPENDENT — each band's intent stands on its own — so
    // a failure on one op must NOT prevent the others from being ATTEMPTED (e.g. a
    // failed Standard update must not silently swallow the cleared Peak's delete).
    // We collect the FIRST error and surface it honestly; the rules are reloaded
    // from the server only when every op succeeded (no optimistic mutation, so the
    // form is preserved on any failure). NO advanced rule_id is ever in `ops`.
    const ordered = [
      ...ops.filter((o) => o.kind === "delete"),
      ...ops.filter((o) => o.kind === "update"),
      ...ops.filter((o) => o.kind === "create"),
    ];
    saveInFlight.current = true;
    setSaving(true);
    let firstError: unknown = null;
    try {
      for (const op of ordered) {
        try {
          if (op.kind === "create")
            await createPricingRule(facilityId, op.body);
          else if (op.kind === "update")
            await updatePricingRule(op.ruleId, op.body);
          else await deletePricingRule(op.ruleId);
        } catch (err) {
          if (firstError === null) firstError = err;
        }
      }
      if (firstError !== null) {
        // Honest failure — a 403 (RBAC) or 422 (validation) surfaces the backend's
        // message VERBATIM via an inline role=alert AND a native notification. The form is
        // preserved (no refetch); nothing is fabricated.
        const message =
          firstError instanceof Error
            ? firstError.message
            : "Could not save rates.";
        setError(message);
        setReadinessAction(
          isApiError(firstError) &&
            firstError.code === "FACILITY_PAYMENTS_UNAVAILABLE"
            ? paymentReadinessAction(firstError)
            : null,
        );
        notifications.error(message);
        return;
      }
      notifications.success("Rates saved.");
      await onSaved();
    } finally {
      saveInFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      {error ? (
        <Alert severity="error">
          <Typography variant="body2">{error}</Typography>
          {readinessAction ? (
            <MuiLink
              component={RouterLink}
              to={readinessAction.href}
              sx={{ display: "inline-block", fontWeight: 700, mt: 0.5 }}
            >
              {readinessAction.label}
            </MuiLink>
          ) : null}
        </Alert>
      ) : null}

      {advancedCount > 0 ? (
        <Paper
          variant="outlined"
          sx={{ borderStyle: "dashed", px: 2, py: 1.5 }}
        >
          <Typography color="text.secondary" variant="body2">
            Custom rules ({advancedCount}) — edit in Advanced.
          </Typography>
        </Paper>
      ) : null}

      <Stack spacing={3}>
        {bandDefs.map((band) => {
          // Peak is the one windowed band — it renders the start/end time inputs
          // beneath its price field. Every other band is a plain price field.
          if (band.key === "peak") {
            return (
              <Stack key={band.key} spacing={1.5}>
                <BandPriceField
                  band={band}
                  value={bands.peak.price}
                  currency={currency}
                  help={bandHelp("peak", config.chargeUnit)}
                  disabled={disabled}
                  onChange={(price) => setBand("peak", { price })}
                />
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                  }}
                >
                  <TextField
                    disabled={disabled}
                    id="simple-peak-start"
                    label="Peak start time"
                    onChange={(event) =>
                      setBand("peak", { startTime: event.target.value })
                    }
                    placeholder="17:00"
                    size="small"
                    value={bands.peak.startTime}
                  />
                  <TextField
                    disabled={disabled}
                    id="simple-peak-end"
                    label="Peak end time"
                    onChange={(event) =>
                      setBand("peak", { endTime: event.target.value })
                    }
                    placeholder="22:00"
                    size="small"
                    value={bands.peak.endTime}
                  />
                </Box>
              </Stack>
            );
          }
          return (
            <BandPriceField
              key={band.key}
              band={band}
              value={bands[band.key].price}
              currency={currency}
              required={!band.optional}
              help={bandHelp(band.key, config.chargeUnit)}
              disabled={disabled}
              onChange={(price) => setBand(band.key, { price })}
            />
          );
        })}
      </Stack>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          disabled={disabled || saving}
          onClick={handleSave}
          size="small"
          variant="contained"
        >
          {saving ? "Saving…" : "Save rates"}
        </Button>
      </Box>
    </Stack>
  );
}

// A single band's price input + its label + a one-line operator help. The price
// is a raw Decimal string (the operator types e.g. "25.00"); it is forwarded
// VERBATIM. The currency code is shown as a prefix hint only (no arithmetic).
function BandPriceField({
  band,
  value,
  currency,
  required,
  help,
  disabled,
  onChange,
}: {
  band: BandDef;
  value: string;
  currency: string;
  required?: boolean;
  help?: string;
  disabled?: boolean;
  onChange: (price: string) => void;
}) {
  const id = `simple-${band.key}-price`;
  return (
    <Stack spacing={1}>
      <TextField
        disabled={disabled}
        fullWidth
        id={id}
        label={`${band.name}${required ? "" : " (optional)"}`}
        onChange={(event) => onChange(event.target.value)}
        placeholder={currency === "USD" ? "25.00" : "0.00"}
        size="small"
        slotProps={{
          htmlInput: {
            "aria-label": band.priceLabel,
            inputMode: "decimal",
          },
        }}
        value={value}
      />
      {help ? (
        <Typography color="text.secondary" variant="caption">
          {help}
        </Typography>
      ) : null}
    </Stack>
  );
}
